// src/pages/Sales/BroadcastPanel.tsx
import { useEffect, useState } from "react";
import { socket } from "@/lib/socket";
import type {
    BroadcastSession,
    BroadcastListUpdate,
    BroadcastChatMessage,
} from "@/types/socketEvents";

const ROOM = "motor-ac";

type StartBroadcastAck =
    | { ok: true; broadcast: BroadcastSession }
    | { ok: false; reason: string };
    
const BroadcastPanel = () => {
    const [role, setRole] = useState<"dealer" | "sales">("sales");
    const [name, setName] = useState("");
    const [persist, setPersist] = useState(false);
    const [broadcasts, setBroadcasts] = useState<BroadcastSession[]>([]);
    const [chatMessages, setChatMessages] = useState<Record<string, BroadcastChatMessage[]>>({});
    const [chatInput, setChatInput] = useState("");
    const [logs, setLogs] = useState<string[]>([]); // 👈 debug log console

    useEffect(() => {
        socket.emit("joinDealer", ROOM);
        socket.emit("getBroadcasts", { dealerRoom: ROOM });

        const addLog = (msg: string) => setLogs((p) => [...p, msg]);

        socket.on("connect", () => addLog(`⚡ Connected: ${socket.id}`));
        socket.on("welcome", (data) => addLog(`Server: ${data.msg}`));

        socket.on("broadcastListUpdated", (data: BroadcastListUpdate) => {
            setBroadcasts(data.broadcasts);
            addLog(`📡 broadcastListUpdated (${data.broadcasts.length} total)`);
        });

        socket.on("broadcastChat", (msg: BroadcastChatMessage) => {
            setChatMessages((prev) => {
                const list = prev[msg.broadcastId] || [];
                return { ...prev, [msg.broadcastId]: [...list, msg] };
            });
        });

        return () => {
            socket.off("connect");
            socket.off("welcome");
            socket.off("broadcastListUpdated");
            socket.off("broadcastChat");
        };
    }, []);

    const startBroadcast = () => {
        socket.emit(
            "startBroadcast",
            {
                dealerRoom: ROOM,
                role,
                name: name || (role === "dealer" ? "Dealer Broadcast" : "Sales Broadcast"),
                dealerId: 101,
                siteId: "app.idscrm",
                title: name || "Live Broadcast",
                description: "Live broadcast via IDSCRM",
                persist,
            },
            (res: StartBroadcastAck) => {
                if (!res.ok) {
                    setLogs((p) => [...p, `⚠️ startBroadcast rejected: ${res.reason}`]);
                }
            }
        );
    };
;

    const endBroadcast = (id: string) => {
        socket.emit("endBroadcast", { dealerRoom: ROOM, broadcastId: id });
    };

    const sendChat = (broadcastId: string) => {
        if (!chatInput.trim()) return;
        socket.emit("broadcastChat", {
            dealerRoom: ROOM,
            broadcastId,
            from: name.trim() || (role === "dealer" ? "Dealer" : "Salesperson"),
            role,
            message: chatInput.trim(),
        });
        setChatInput("");
    };

    return (
        <div className="p-6 space-y-6">
            <h1 className="text-2xl font-bold">Broadcast Panel</h1>

            {/* Controls */}
            <div className="flex flex-col gap-2">
                <div className="flex gap-2">
                    <select
                        value={role}
                        onChange={(e) => setRole(e.target.value as "dealer" | "sales")}
                        className="border rounded px-2 py-1"
                    >
                        <option value="dealer">Dealer</option>
                        <option value="sales">Sales</option>
                    </select>
                    <input
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="Broadcast name"
                        className="border rounded px-2 py-1 flex-1"
                    />
                    <button
                        onClick={startBroadcast}
                        className="bg-green-600 text-white px-4 py-2 rounded"
                    >
                        Go Live
                    </button>
                </div>

                <label className="flex items-center gap-2 text-sm">
                    <input
                        type="checkbox"
                        checked={persist}
                        onChange={(e) => setPersist(e.target.checked)}
                    />
                    Keep this broadcast in history after ending
                </label>
            </div>

            {/* Live Broadcasts */}
            <section>
                <h2 className="text-xl font-semibold mb-2">Live Broadcasts</h2>
                {broadcasts.filter((b) => b.isLive).length === 0 && (
                    <p className="text-sm text-gray-500">No broadcasts live right now.</p>
                )}
                {broadcasts
                    .filter((b) => b.isLive)
                    .map((b) => (
                        <div key={b.id} className="border rounded p-2 space-y-2 bg-green-50">
                            <div className="flex items-center justify-between">
                                <span>
                                    🔴 Live [{b.role}] {b.name}{" "}
                                    {b.vehicleId ? `(Vehicle: ${b.vehicleId})` : ""}{" "}
                                    {b.siteId ? `| Site: ${b.siteId}` : ""}
                                </span>
                                <button
                                    onClick={() => endBroadcast(b.id)}
                                    className="bg-red-500 text-white px-3 py-1 rounded"
                                >
                                    End
                                </button>
                            </div>

                            {/* Chat */}
                            <div className="border rounded p-2 h-32 overflow-y-auto text-sm bg-gray-50">
                                {(chatMessages[b.id] || []).map((m) => (
                                    <div key={m.ts}>
                                        <strong>[{m.role}] {m.from}:</strong> {m.message}
                                    </div>
                                ))}
                            </div>
                            <div className="flex gap-2">
                                <input
                                    value={chatInput}
                                    onChange={(e) => setChatInput(e.target.value)}
                                    placeholder="Type a message..."
                                    className="flex-1 border rounded px-2 py-1"
                                />
                                <button
                                    onClick={() => sendChat(b.id)}
                                    className="bg-blue-600 text-white px-3 py-1 rounded"
                                >
                                    Send
                                </button>
                            </div>
                        </div>
                    ))}
            </section>

            {/* Past Broadcasts */}
            <section>
                <h2 className="text-xl font-semibold mb-2">Past Broadcasts</h2>
                {broadcasts.filter((b) => !b.isLive).length === 0 && (
                    <p className="text-sm text-gray-500">No past broadcasts.</p>
                )}
                {broadcasts
                    .filter((b) => !b.isLive)
                    .map((b) => (
                        <div key={b.id} className="border rounded p-2 space-y-2 bg-gray-100">
                            <div className="flex items-center justify-between">
                                <span>
                                    ⏹ Ended [{b.role}] {b.name}{" "}
                                    {b.vehicleId ? `(Vehicle: ${b.vehicleId})` : ""}{" "}
                                    {b.siteId ? `| Site: ${b.siteId}` : ""}
                                    {b.persist && (
                                        <span className="ml-2 inline-flex items-center text-xs text-purple-600 font-semibold">
                                            📼 Replay Available
                                        </span>
                                    )}
                                </span>
                                {b.endTime && (
                                    <span className="text-xs text-gray-500">
                                        Ended at {new Date(b.endTime).toLocaleTimeString()}
                                    </span>
                                )}
                            </div>
                        </div>
                    ))}
            </section>

            {/* Debug Log */}
            <section>
                <h2 className="text-xl font-semibold mb-2">Debug Log</h2>
                <div className="border rounded-lg p-3 h-40 overflow-y-auto bg-gray-900 text-green-400 text-xs font-mono">
                    {logs.map((m, i) => (
                        <div key={i}>{m}</div>
                    ))}
                </div>
            </section>
        </div>
    );
};

export default BroadcastPanel;
